"use client";

import { useTranslation } from "@guru/shared/i18n";
import { Clock, Mail, MessageCircle, ShieldCheck } from "lucide-react";
import { ScrollReveal } from "@/components/public/scroll-reveal";
import { WhatsAppContactCard } from "@/components/public/whatsapp-contact-card";
import { useSupportStatus } from "@/lib/marketing/support-hours";

export function ContactPageContent() {
  const { t } = useTranslation();
  const { online } = useSupportStatus();

  const c = t.contact;
  const eyebrow = c?.eyebrow ?? "Talk to us";
  const title = c?.title ?? "Contact Us";
  const subtitle =
    c?.subtitle ??
    "Have questions about EtheraGuru? We're here to help. Reach out via WhatsApp and we'll respond as soon as possible.";
  const responseTime = c?.responseTime ?? "We usually reply within a couple of hours";
  const availability = c?.whatsappAvailability ?? "Mon - Sat, 12:30 PM - 9:30 PM IST";
  const statusOnline = c?.statusOnline ?? "Online now";
  const statusAway = c?.statusAway ?? "Away right now";
  const realPersonTitle = c?.realPersonTitle ?? "A real person, not a bot";
  const realPersonBody =
    c?.realPersonBody ?? "Your message reaches our team directly — no ticket queue, no runaround.";
  const quickRepliesTitle = c?.quickRepliesTitle ?? "Quick to reply";
  const privacyTitle = c?.privacyTitle ?? "Private by default";
  const privacyBody =
    c?.privacyBody ?? "We only use your message to help you. Never shared, never spammed.";
  const emailTitle = c?.emailFallbackTitle ?? "Prefer email?";

  const liveStatus = online === false ? statusAway : statusOnline;

  const reasons = [
    { icon: MessageCircle, title: realPersonTitle, body: realPersonBody },
    { icon: Clock, title: quickRepliesTitle, body: responseTime },
    { icon: ShieldCheck, title: privacyTitle, body: privacyBody },
  ];

  return (
    <section className="relative overflow-clip py-20 sm:py-24">
      {/* Ambient ledger texture + warm glow, matching the homepage sections */}
      <div
        className="ledger-grid ledger-grid-fade pointer-events-none absolute inset-0"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute left-1/2 top-8 h-64 w-[42rem] max-w-full -translate-x-1/2 rounded-full bg-brand/10 blur-3xl"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
        <ScrollReveal threshold={0.3} className="mx-auto max-w-2xl text-center">
          <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-brand-deep">
            {eyebrow}
          </p>
          <h1 className="text-balance text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            {title}
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-pretty text-lg text-muted-foreground">
            {subtitle}
          </p>
        </ScrollReveal>

        <div className="mt-12 grid items-start gap-6 lg:grid-cols-[1fr_minmax(0,27rem)] lg:gap-10">
          {/* Trust panel — why it's worth reaching out */}
          <ScrollReveal threshold={0.2} className="flex flex-col gap-4">
            {/* Live status banner */}
            <div className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm">
              <span className="relative flex size-3">
                {online !== false ? (
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-positive opacity-75 motion-reduce:hidden" />
                ) : null}
                <span
                  className={`relative inline-flex size-3 rounded-full ${
                    online === false ? "bg-amber-400" : "bg-positive"
                  }`}
                />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground">{liveStatus}</p>
                <p className="text-xs text-muted-foreground">{availability}</p>
              </div>
            </div>

            <ul className="flex flex-col gap-3">
              {reasons.map((reason) => (
                <li
                  key={reason.title}
                  className="stagger-child flex items-start gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
                >
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand-deep ring-1 ring-brand/15">
                    <reason.icon className="size-5" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground">{reason.title}</p>
                    <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground">
                      {reason.body}
                    </p>
                  </div>
                </li>
              ))}
            </ul>

            {/* Email fallback */}
            <a
              href="mailto:contact@etheraedge.com"
              className="group flex items-center gap-3 rounded-2xl border border-dashed border-border bg-card/60 p-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-brand/50 hover:shadow-md"
            >
              <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-secondary text-muted-foreground transition-colors duration-300 group-hover:bg-brand/10 group-hover:text-brand-deep">
                <Mail className="size-5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground">{emailTitle}</p>
                <p className="truncate text-sm text-muted-foreground underline-offset-4 group-hover:underline">
                  contact@etheraedge.com
                </p>
              </div>
            </a>
          </ScrollReveal>

          {/* The live conversation */}
          <ScrollReveal delay={150} threshold={0.2}>
            <WhatsAppContactCard />
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
